import React, { useState, useEffect } from 'react';
import { knowledgeBaseApi } from '../../services/knowledgeBaseApi';
import './VersionHistory.css';

interface Version {
  version: number;
  is_active: boolean;
  modified_by: string;
  modified_at: string;
  change_type: string;
  change_request?: string;
  change_summary?: string;
}

interface VersionHistoryProps {
  configType: string;
  configName: string;
  currentVersion?: number;
  onVersionRestore?: (version: number) => void;
  onVersionView?: (version: number) => void;
}

const VersionHistory: React.FC<VersionHistoryProps> = ({
  configType,
  configName,
  currentVersion,
  onVersionRestore,
  onVersionView
}) => {
  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedVersion, setExpandedVersion] = useState<number | null>(null);
  const [viewingVersion, setViewingVersion] = useState<any>(null);
  const [showViewModal, setShowViewModal] = useState(false);

  useEffect(() => {
    loadVersionHistory();
  }, [configType, configName]);

  const loadVersionHistory = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await knowledgeBaseApi.getVersionHistory(configType, configName);
      if (result.success) {
        setVersions(result.versions);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load version history');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (version: number) => {
    if (!window.confirm(`Are you sure you want to restore version ${version}? This will create a new version with the content from version ${version}.`)) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await knowledgeBaseApi.restoreVersion(configType, configName, version);
      if (result.success) {
        // Reload history to show new version
        await loadVersionHistory();
        if (onVersionRestore) {
          onVersionRestore(version);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to restore version');
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (version: number) => {
    setLoading(true);
    setError(null);

    try {
      const result = await knowledgeBaseApi.getSpecificVersion(configType, configName, version);
      if (result.success) {
        setViewingVersion(result.config);
        setShowViewModal(true);
        if (onVersionView) {
          onVersionView(version);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load version');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getChangeTypeIcon = (changeType: string) => {
    switch (changeType) {
      case 'ai_edit':
        return '✨';
      case 'restored':
        return '↩️';
      case 'initial':
        return '🎬';
      default:
        return '✏️';
    }
  };

  const getChangeTypeLabel = (changeType: string) => {
    switch (changeType) {
      case 'ai_edit':
        return 'AI Edit';
      case 'restored':
        return 'Restored';
      case 'initial':
        return 'Initial';
      default:
        return 'Manual';
    }
  };

  if (loading && versions.length === 0) {
    return <div className="version-history-loading">Loading version history...</div>;
  }

  if (error) {
    return <div className="version-history-error">Error: {error}</div>;
  }

  return (
    <div className="version-history">
      <div className="version-history-header">
        <h3>Version History</h3>
        <span className="version-count">{versions.length} versions</span>
      </div>

      <div className="version-timeline">
        {versions.map((version, index) => (
          <div 
            key={version.version}
            className={`version-item ${version.is_active ? 'active' : ''} ${expandedVersion === version.version ? 'expanded' : ''}`}
          >
            <div 
              className="version-header"
              onClick={() => setExpandedVersion(expandedVersion === version.version ? null : version.version)}
            >
              <div className="version-number">
                v{version.version}
                {version.is_active && <span className="current-badge">Current</span>}
              </div>
              <div className="version-meta">
                <span className="version-type">
                  {getChangeTypeIcon(version.change_type)} {getChangeTypeLabel(version.change_type)}
                </span>
                <span className="version-author">by {version.modified_by || 'unknown'}</span>
                <span className="version-date">{formatDate(version.modified_at)}</span>
              </div>
              <div className="version-toggle">
                {expandedVersion === version.version ? '▼' : '▶'}
              </div>
            </div>

            {expandedVersion === version.version && (
              <div className="version-details">
                {version.change_request && (
                  <div className="version-request">
                    <strong>Change Request:</strong> {version.change_request}
                  </div>
                )}
                {version.change_summary && (
                  <div className="version-summary">
                    <strong>Changes:</strong>
                    <div className="summary-content">
                      {version.change_summary.split('\n').map((line, i) => (
                        <div key={i}>{line}</div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="version-actions">
                  <button 
                    className="btn-view-version"
                    onClick={() => handleView(version.version)}
                    disabled={loading}
                  >
                    View
                  </button>
                  {!version.is_active && (
                    <button 
                      className="btn-restore-version"
                      onClick={() => handleRestore(version.version)}
                      disabled={loading}
                    >
                      Restore
                    </button>
                  )}
                </div>
              </div>
            )}

            {index < versions.length - 1 && <div className="version-connector" />}
          </div>
        ))}
      </div>

      {showViewModal && viewingVersion && (
        <div className="version-modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="version-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Version {viewingVersion.version}</h3>
              <button className="modal-close" onClick={() => setShowViewModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <pre className="version-content">
                {JSON.stringify(viewingVersion.config_data, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VersionHistory;